import React from 'react';

// Button component styled to match Figma specs
// Variants:
// - primary: light surface pill with accent text and optional trailing icon circle
// - danger: red pill with light text
// - counter: "Selected: N" style (bold label + regular count)
import { motion } from 'framer-motion';
import Icon from './Icon';

export default function Button({
  children,
  onClick,
  type = 'button',
  disabled = false,
  className = '',
  // figma-driven props
  variant = 'primary', // 'primary' | 'danger' | 'counter' | 'glass'
  trailingIconName, // Icon name from Icon registry
  withCircle = true, // whether to render the dark circle capsule
  count, // number for counter variant
}) {
  const isDanger = variant === 'danger';
  const isCounter = variant === 'counter';
  const isGlass = variant === 'glass';

  const baseClasses = [
    'inline-flex',
    'items-center',
    'gap-3',
    'rounded-full',
    'px-3',
    'py-[9px]',
    'select-none'
  ];

  const bg = isDanger ? '#FF6E6E' : (isGlass ? 'rgba(255,255,255,0.06)' : '#FFFFFF');
  const textColor = isDanger ? 'var(--color-surface, #FFF7EF)' : (isGlass ? '#FFFFFF' : 'var(--color-accent, #56371F)');
  const outline = isDanger ? '1px var(--color-surface, #FFF7EF) solid' : (isGlass ? 'none' : '1px #56371F solid');

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={[...baseClasses, className, disabled ? 'opacity-50' : ''].join(' ').trim()}
      whileTap={{ scale: 0.96 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      style={{
        background: bg,
        color: textColor,
        outline,
        outlineOffset: '-1px',
        borderRadius: 100,
        border: isGlass ? '1px solid rgba(255,255,255,0.35)' : 'none',
        backdropFilter: isGlass ? 'blur(10px)' : undefined,
        WebkitBackdropFilter: isGlass ? 'blur(10px)' : undefined,
      }}
    >
      {isCounter ? (
        <>
          <span style={{
            color: 'var(--color-accent, #56371F)',
            fontSize: 16,
            fontFamily: 'Poppins',
            fontWeight: 500,
            lineHeight: 1,
          }}>Selected:</span>
          <span style={{
            color: '#56371F',
            fontSize: 16,
            fontFamily: 'Poppins',
            fontWeight: 400,
            lineHeight: 1,
          }}>{count}</span>
        </>
      ) : (
        <span style={{
          color: textColor,
          fontSize: 16,
          fontFamily: 'Poppins',
          fontWeight: 500,
          lineHeight: 1,
        }}>
          {children}
        </span>
      )}

      {!isCounter && trailingIconName && !isDanger && (
        withCircle ? (
          <span
            aria-hidden
            style={{
              width: 26,
              height: 26,
              padding: 6,
              background: isGlass ? '#FFFFFF' : 'var(--color-accent, #56371F)',
              borderRadius: 100,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon
              name={trailingIconName}
              size={18}
              color={isGlass ? '#56371F' : '#FFF7EF'}
            />
          </span>
        ) : (
          <Icon name={trailingIconName} size={20} color={textColor} />
        )
      )}
    </motion.button>
  );
}